import Router from 'next/router';

/**
 * How the page an event fires on was reached, and where this tab's visit started.
 *
 * `document.referrer` does not change on a Next.js client navigation, so the old `data.referrer`
 * keeps saying "google.com" on every page of a visit that started on Google. These fields let a
 * Splunk search tell "this page came from Google" from "this visit came from Google" without
 * joining indexes on terminal_id.
 *
 * Kept free of heavy imports: splunk.ts imports it, and splunk.ts is imported everywhere.
 */

export const SESSION_ENTRY_KEY = 'p24_attr_session';

export type SessionEntry = {
  /** document.referrer of the first page of this tab's visit ('' = direct / app / no referrer). */
  entry_referrer: string;
  /** Pathname of that first page. */
  landing_path: string;
  /** When it was captured; with terminal_id it identifies one visit (tab). */
  entry_at: string;
};

export type NavigationContext = SessionEntry & {
  /** 'hard' = this document's first page; 'client' = reached by a Next.js client navigation. */
  nav_type: 'hard' | 'client';
  /** PerformanceNavigationTiming.type of the document: navigate | reload | back_forward | prerender. */
  load_type: string;
  /** Pathname before the last client navigation ('' on a hard navigation). */
  prev_path: string;
};

let memoryEntry: SessionEntry | null = null;

const readStoredEntry = (): SessionEntry | null => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_ENTRY_KEY);
    return raw ? (JSON.parse(raw) as SessionEntry) : null;
  } catch {
    return null;
  }
};

/** Once per tab (sessionStorage survives reloads, not new tabs). Later calls return the stored entry. */
export function captureSessionEntry(): SessionEntry | null {
  if (typeof window === 'undefined') return null;
  const known = memoryEntry ?? readStoredEntry();
  if (known) return (memoryEntry = known);

  memoryEntry = {
    entry_referrer: document.referrer || '',
    landing_path: window.location.pathname,
    entry_at: new Date().toISOString(),
  };
  try {
    window.sessionStorage.setItem(SESSION_ENTRY_KEY, JSON.stringify(memoryEntry));
  } catch {
    // Private mode / blocked storage: the entry lives for this document only.
  }
  return memoryEntry;
}

type RouterEvents = {
  on(event: any, handler: (...args: any[]) => void): void;
};

let tracking = false;
let navType: NavigationContext['nav_type'] = 'hard';
let prevPath = '';
let currentPath = '';
let undo: { navType: NavigationContext['nav_type']; prevPath: string; currentPath: string } | null = null;

const pathOf = (url: string) => {
  try {
    return new URL(url, window.location.href).pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
};

/**
 * Updates at routeChangeStart, not routeChangeComplete: the new page's effects (where page-view
 * events fire) can run before routeChangeComplete. A cancelled or failed navigation is undone.
 */
export function trackNavigation(events: RouterEvents = Router.events): void {
  if (tracking || typeof window === 'undefined') return;
  tracking = true;
  currentPath = window.location.pathname;

  events.on('routeChangeStart', (url: string) => {
    undo = { navType, prevPath, currentPath };
    navType = 'client';
    prevPath = currentPath;
    currentPath = pathOf(url);
  });
  events.on('routeChangeComplete', () => {
    undo = null;
  });
  events.on('routeChangeError', () => {
    if (!undo) return;
    ({ navType, prevPath, currentPath } = undo);
    undo = null;
  });
}

const documentLoadType = (): string => {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entry?.type ?? '';
  } catch {
    return '';
  }
};

/** Fields merged into every event of the profile, search and booking Splunk indexes. */
export function getNavigationContext(): NavigationContext | Record<string, never> {
  if (typeof window === 'undefined') return {};
  trackNavigation();
  const entry = captureSessionEntry();
  return {
    nav_type: navType,
    load_type: documentLoadType(),
    prev_path: prevPath,
    entry_referrer: entry?.entry_referrer ?? '',
    landing_path: entry?.landing_path ?? '',
    entry_at: entry?.entry_at ?? '',
  };
}
